import { useState, useEffect } from 'react'
import { useNavigate, Link, useLocation, useSearchParams } from 'react-router-dom'
import { FaArrowLeft } from 'react-icons/fa'
import { authService, sendCompleteRegistrationEvent, analyticsService } from '../services'
import { useTranslation } from 'react-i18next'
import { usePageTitle, setDocumentTitle } from '../utils/translation'
import { toast } from 'react-hot-toast'
import GoogleAuthButton from '../components/auth/GoogleAuthButton'

export default function Register() {
  const { t } = useTranslation()
  const [registrationType, setRegistrationType] = useState<'email' | 'phone'>('phone')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showRequired, setShowRequired] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [showEmailExistsError, setShowEmailExistsError] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  
  // Get translated page title
  const pageTitle = usePageTitle('register.title', 'Create Account')
  
  // Extract returnTo from location state if it exists
  const returnToFromState = location.state?.returnTo
  const returnToFromQuery = searchParams.get('returnTo')
  const returnTo = returnToFromState || returnToFromQuery || '/flowers'

  // Check if user is already authenticated
  useEffect(() => {
    // Set document title
    setDocumentTitle(pageTitle)
    
    if (authService.isAuthenticated()) {
      // User has a valid token, redirect to homepage
      navigate('/flowers')
      return
    }

    // Check if returning from Google OAuth redirect with an error
    const error = searchParams.get('error')
    if (error) {
      toast.error(decodeURIComponent(error))
    }

    // Check if returning from Google OAuth redirect with auth token in cookie
    const tokenFromCookie = document.cookie
      .split('; ')
      .find(row => row.startsWith('authToken='))
    
    if (tokenFromCookie) {
      // We have a token in the cookie set by the server
      // Save it to localStorage and redirect
      const token = tokenFromCookie.split('=')[1]
      localStorage.setItem('authToken', token)
      localStorage.setItem('lastActivityTime', Date.now().toString())
      navigate(returnTo)
      return
    }

    // Initialize Google Auth
    authService.initGoogleAuth()
  }, [navigate, pageTitle, searchParams, returnTo, t])

  const handleBack = () => {
    navigate('/welcome')
  }

  const handleRegistrationTypeChange = (type: 'email' | 'phone') => {
    setRegistrationType(type)
    setShowRequired(false)
    setError('')
    setShowEmailExistsError(false)
    console.log('Registration type changed to:', type)
  }

  const handleGoToLogin = () => {
    // Navigate to login page with email pre-filled
    navigate('/login', { 
      state: { 
        prefilledEmail: email,
        returnTo: returnTo 
      }
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (registrationType === 'email') {
      // Email registration requires email and password
      if (!email || !password) {
        setShowRequired(true)
        return
      }
      
      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(email)) {
        setError(t('register.invalidEmail'))
        return
      }
      
      // Password validation
      if (password.length < 8) {
        setError(t('register.passwordTooShort'))
        return
      }
      
      setLoading(true)
      setError('')
      setShowEmailExistsError(false)
      
      try {
        // Call the register API with email
        const response = await authService.register(email, password, '', '') // Empty name and phone
        
        // The token is already stored in authService.register method
        console.log('User registered and automatically logged in');

        // Track account creation with Google Analytics
        try {
          analyticsService.trackAccountCreation('email', response.user?.id);
        } catch (analyticsError) {
          console.error('Error tracking account creation:', analyticsError);
          // Don't block the registration flow if analytics tracking fails
        }

        // Send Facebook Conversions API event for registration
        try {
          await sendCompleteRegistrationEvent({
            email: email,
            clientUserAgent: navigator.userAgent
          }, window.location.href);
        } catch (eventError) {
          console.error('Error sending Facebook event:', eventError);
          // Don't block the registration flow if event tracking fails
        }
        
        // Directly navigate to the returnTo page instead of showing verification success
        toast.success(t('register.registrationSuccess'))
        navigate(returnTo)
      } catch (err: any) {
        console.error('Registration error:', err);
        console.error('Error response status:', err.response?.status);
        console.error('Error response data:', err.response?.data);
        console.error('Full error object:', JSON.stringify({
          status: err.response?.status,
          data: err.response?.data,
          message: err.message,
          stack: err.stack
        }, null, 2));
        
        // Extract error message from the response if available
        let errorMessage = t('register.generalError');
        let showSpecialEmailError = false;
        
        if (err.response) {
          const responseError = err.response.data?.error;
          const responseMessage = err.response.data?.message;
          const status = err.response.status;
          
          // Check both error and message fields
          const errorText = responseError || responseMessage || '';
          console.log('Error text from response:', errorText);
          console.log('Response status:', status);
          
          if (errorText) {
            if (errorText.includes('User with this email already exists') || 
                errorText.includes('email already exists') ||
                errorText.includes('email already registered')) {
              // Show special error state for existing email
              console.log('✅ Detected existing email error, showing special UI');
              showSpecialEmailError = true;
            } else if (errorText.includes('password')) {
              errorMessage = t('register.passwordTooWeak');
            } else {
              errorMessage = errorText;
            }
          }
          
          // Fallback: If this is a 401 error during email registration, it's likely an existing email issue
          if (status === 401 && registrationType === 'email' && !showSpecialEmailError) {
            console.log('🔍 Fallback: 401 error during email registration, assuming email exists');
            showSpecialEmailError = true;
          }
        } else if (err.message) {
          console.log('Error message from exception:', err.message);
          if (err.message.includes('User with this email already exists') || 
              err.message.includes('email already exists') ||
              err.message.includes('email already registered')) {
            console.log('✅ Detected existing email error in message, showing special UI');
            showSpecialEmailError = true;
          } else {
            errorMessage = err.message;
          }
        }
        
        console.log('Final decision - showSpecialEmailError:', showSpecialEmailError);
        
        if (showSpecialEmailError) {
          console.log('🎯 Setting showEmailExistsError to true');
          setShowEmailExistsError(true);
          setError(''); // Clear any regular error
        } else {
          console.log('❌ Setting regular error:', errorMessage);
          setError(errorMessage);
          setShowEmailExistsError(false); // Clear special error state
        }
      } finally {
        setLoading(false)
      }
    } else {
      // Phone registration requires phone number
      if (!phone) {
        setShowRequired(true)
        return
      }
      
      // Basic phone validation (simple check for now)
      if (phone.length < 9) {
        setError(t('register.invalidPhoneNumber'))
        return
      }
      
      setLoading(true)
      setError('')
      
      try {
        // Add +66 prefix to the phone number
        const fullPhoneNumber = `66${phone}`;
        
        // Send verification code to the phone number
        const response = await authService.registerWithPhone(fullPhoneNumber);
        
        // Check if this is an existing user (already registered phone)
        const registrationType = response.existingUser === true ? 'login' : 'phone';
        
        // Track account creation attempt with phone
        try {
          analyticsService.trackAccountCreation('phone');
        } catch (analyticsError) {
          console.error('Error tracking phone registration:', analyticsError);
          // Don't block the registration flow if analytics tracking fails
        }

        // Navigate to verification page with appropriate type and returnTo path
        navigate('/phone-verify', { 
          state: { 
            registrationType: registrationType,
            contactInfo: fullPhoneNumber,
            returnTo: returnTo
          } 
        })
      } catch (err: any) {
        console.error('Registration error:', err);
        
        // Display the specific error message if available
        let errorMessage = t('register.verificationCodeError');
        
        if (err.message) {
          errorMessage = err.message;
        } else if (err.response && err.response.data) {
          // Handle API error responses
          const responseError = err.response.data.error || err.response.data.message;
          if (responseError) {
            errorMessage = responseError;
          }
        }
        
        setError(errorMessage);
      } finally {
        setLoading(false)
      }
    }
  }

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <div className="p-6 flex-1">
        {/* Navbar with circular back button */}
        <div className="flex items-center mb-8">
          <button onClick={handleBack} className="mr-4 p-2 bg-gray-200 rounded-full">
            <FaArrowLeft className="text-gray-700 text-xl font-bold" />
          </button>
        </div>

        <div className="flex items-center justify-between mb-2">
          <h2 className="text-2xl font-semibold">{t('register.title')}</h2>
          <Link 
            to={`/login${returnTo ? `?returnTo=${encodeURIComponent(returnTo)}` : ''}`}
            className="text-sm text-blue-600 font-medium hover:text-blue-800 transition-colors underline"
          >
            {t('register.switchToLogin', 'Log In')}
          </Link>
        </div>

        <p className="text-gray-700 text-sm mb-4">
          {t('register.subtitle')}
        </p>

        {/* Google login button */}
        <GoogleAuthButton 
          mode="register"
          returnTo={returnTo}
          onError={(error) => setError(error.message)}
        />
        
        <div className="flex items-center justify-center mb-4">
          <div className="flex-1 h-[1px] bg-gray-300"></div>
          <span className="mx-3 text-gray-500 text-sm">{t('register.or')}</span>
          <div className="flex-1 h-[1px] bg-gray-300"></div>
        </div>

        {/* Toggle between email and phone registration */}
        <div className="flex mb-6 gap-3">
          <button
            onClick={() => handleRegistrationTypeChange('phone')}
            className={`flex-1 py-3.5 text-center rounded-[12px] border ${
              registrationType === 'phone' 
                ? 'border-[#F9D348] bg-[#FFFAEB] text-black font-medium' 
                : 'border-gray-200 bg-white text-gray-500'
            }`}
          >
            {t('register.phone')}
          </button>
          <button
            onClick={() => handleRegistrationTypeChange('email')}
            className={`flex-1 py-3.5 text-center rounded-[12px] border ${
              registrationType === 'email' 
                ? 'border-[#F9D348] bg-[#FFFAEB] text-black font-medium' 
                : 'border-gray-200 bg-white text-gray-500'
            }`}
          >
            {t('register.email')}
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col h-full">
          {registrationType === 'email' ? (
            <>
              <input
                type="email"
                placeholder={t('register.emailPlaceholder')}
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value)
                  setShowRequired(false)
                }}
                className="w-full p-4 border border-gray-300 rounded-[16px] mb-4 focus:outline-none focus:border-gray-500"
              />
              
              <input
                type="password"
                placeholder={t('register.passwordPlaceholder')}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value)
                  setShowRequired(false)
                }}
                className="w-full p-4 border border-gray-300 rounded-[16px] mb-4 focus:outline-none focus:border-gray-500"
              />
            </>
          ) : (
            <>
              <div className="relative w-full mb-1">
                <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-700 font-medium">+66</div>
                <input
                  type="tel"
                  placeholder={t('register.phonePlaceholder')}
                  value={phone}
                  onChange={(e) => {
                    // Remove any non-digit characters
                    const value = e.target.value.replace(/\D/g, '');
                    setPhone(value);
                    setShowRequired(false);
                  }}
                  className="w-full p-4 pl-14 border border-gray-300 rounded-[16px] focus:outline-none focus:border-gray-500"
                />
              </div>
              <p className="text-gray-500 text-xs mb-2">{t('register.thaiPhoneOnly')}</p>
            </>
          )}
          
          {showRequired && (
            <span className="text-red-500 text-sm mb-4">
              {registrationType === 'email' 
                ? t('register.emailPasswordRequired') 
                : t('register.phoneRequired')}
            </span>
          )}
          
          {error && (
            <span className="text-red-500 text-sm mb-4">{error}</span>
          )}

          {showEmailExistsError && (
            <div className="bg-orange-50 border border-orange-200 rounded-[12px] p-4 mb-4">
              <p className="text-orange-800 text-sm mb-3">
                {t('register.emailAlreadyExists')}
              </p>
              <button
                onClick={handleGoToLogin}
                className="w-full bg-orange-600 text-white px-4 py-2 rounded-[8px] text-sm font-medium hover:bg-orange-700 transition-colors"
              >
                {t('register.goToLogin')}
              </button>
            </div>
          )}
          


          <button 
            type="submit"
            disabled={loading || showEmailExistsError}
            className={`w-full h-[60px] rounded-[16px] font-medium text-black mt-6 ${
              (registrationType === 'email' && email && password) || 
              (registrationType === 'phone' && phone) 
                ? 'bg-[#F9D348]' 
                : 'bg-[#FDEDB6]'
            } ${showEmailExistsError ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {loading 
              ? t('register.processing')
              : registrationType === 'email' 
                ? t('register.continueWithEmail')
                : t('register.sendVerificationCode')}
          </button>
        </form>

        <p className="text-gray-600 text-xs mt-4">
          {t('register.termsAgreement')} <Link to="/terms" className="text-blue-600">{t('register.termsOfService')}</Link> {t('register.and')} <Link to="/privacy" className="text-blue-600">{t('register.privacyPolicy')}</Link>.
        </p>

        <div className="mt-6 text-center">
          <p className="text-gray-600 text-sm">
            {t('register.haveAccount')}{' '}
            <Link 
              to={`/login${returnTo ? `?returnTo=${encodeURIComponent(returnTo)}` : ''}`}
              className="text-blue-600 font-medium hover:text-blue-800 transition-colors underline"
            >
              {t('register.logIn')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}